import type { ReactNode } from 'react';

export interface PenroseShellProps {
  /** Top header strip — populated in turn 0e.2. */
  header?: ReactNode;
  /** Left rail — populated in turn 0e.3. */
  rail?: ReactNode;
  /** Main content slot. */
  main?: ReactNode;
  /**
   * Global agent sidebar — rendered as a fixed right panel (wide viewports)
   * or a slide-up overlay (narrow viewports). Presence triggers `data-has-sidebar`
   * which instructs CSS to reserve space in `.pn-app-main`.
   */
  sidebar?: ReactNode;
  /** Test hook for snapshot specs. */
  'data-testid'?: string;
}

/**
 * PenroseShell — the canonical signed-in shell skeleton.
 *
 * Mirrors the dashboard.html `.shell` grid: a `--rail-w` × 1fr column track
 * crossed by a `--header-h` × 1fr row track, with the rail spanning both rows
 * (rail · header / rail · main). Hairline borders between regions; warm cream
 * canvas (`--bg`).
 *
 * Structural skeleton only — region details (breadcrumbs, identity chip,
 * rail links, etc.) ship in later 0e.* turns.
 */
export function PenroseShell({
  header,
  rail,
  main,
  sidebar,
  'data-testid': testId
}: PenroseShellProps) {
  return (
    <>
      <div
        className="pn-app"
        data-testid={testId ?? 'penrose-shell'}
        data-has-sidebar={sidebar ? '' : undefined}
      >
        <aside className="pn-app-rail" aria-label="Primary navigation">
          {rail}
        </aside>
        <header className="pn-app-header">{header}</header>
        <main className="pn-app-main">{main}</main>
      </div>
      {sidebar}
    </>
  );
}
